/**
 * Hooks para componentes de navegação
 * 
 * Este arquivo contém os hooks para os componentes de navegação
 * utilizados no sistema Mealflow.
 */

/**
 * Hook que gerencia o estado de navegação hierárquica
 * - Persiste estado expandido entre navegações
 * - Mantém diferentes estados por sessão (isolamento de abas)
 * - Sincroniza estado com o servidor
 */
const HierarchyNav = {
  mounted() {
    // Carregar estado armazenado em localStorage para esta sessão
    this.loadExpandedState();
    
    // Observar mudanças nos nodes expandidos
    this.observeExpandedState();
    
    // Adicionar atalhos de teclado para navegação
    this.setupKeyboardShortcuts();
    
    // Registrar manipuladores de evento para fechamento
    this.setupEventHandlers();
  },
  
  // Carregar o estado de expansão salvo localmente
  loadExpandedState() {
    // Obter ID da sessão atual (para isolamento entre abas)
    const sessionId = document.querySelector('meta[name="session-id"]')?.content;
    
    if (!sessionId) return;
    
    try {
      // Carregar configuração específica da sessão
      const savedState = localStorage.getItem(`mealflow-nav-${sessionId}`);
      
      if (savedState) {
        const expandedNodes = JSON.parse(savedState);
        
        // Aplicar estado aos elementos
        expandedNodes.forEach(nodeId => {
          const nodeElement = document.getElementById(nodeId);
          if (nodeElement) {
            // Se o elemento usa AlpineJS, atualizamos a propriedade Alpine
            if (window.Alpine) {
              const alpineData = window.Alpine.$data(nodeElement);
              if (alpineData && 'expanded' in alpineData) {
                alpineData.expanded = true;
              }
            }
          }
        });
      }
    } catch (e) {
      console.error("Erro ao carregar estado da navegação:", e);
    }
  },
  
  // Observa mudanças no estado de expansão e salva
  observeExpandedState() {
    // Usamos MutationObserver para detectar mudanças nos elementos
    // Isso funciona mesmo quando as mudanças são feitas pelo AlpineJS
    this.observer = new MutationObserver(mutations => {
      this.saveExpandedState();
    });
    
    // Observar atributos e estrutura dos nós de navegação
    const navElement = this.el;
    this.observer.observe(navElement, {
      attributes: true,
      attributeFilter: ['x-bind:class', 'class', 'style'],
      subtree: true,
      childList: true
    });
  },
  
  // Salva o estado de expansão atual no localStorage
  saveExpandedState() {
    const sessionId = document.querySelector('meta[name="session-id"]')?.content;
    if (!sessionId) return;
    
    // Coletar todos os nós de nível expandidos
    const expandedNodes = [];
    
    // Se usamos Alpine, podemos verificar propriedades Alpine
    if (window.Alpine) {
      document.querySelectorAll('.nav-level').forEach(node => {
        try {
          const alpineData = window.Alpine.$data(node);
          if (alpineData && alpineData.expanded && node.id) {
            expandedNodes.push(node.id);
          }
        } catch (e) {
          // Ignorar erros - o elemento pode não ter dados Alpine
        }
      });
    }
    
    // Salvar no localStorage
    try {
      localStorage.setItem(`mealflow-nav-${sessionId}`, JSON.stringify(expandedNodes));
      
      // Enviar ao servidor para sincronização entre dispositivos (opcional)
      this.pushEvent("save_nav_state", { expanded_nodes: expandedNodes });
    } catch (e) {
      console.error("Erro ao salvar estado da navegação:", e);
    }
  },
  
  // Configurar atalhos de teclado para navegação
  setupKeyboardShortcuts() {
    document.addEventListener('keydown', e => {
      // Alt + número para navegação rápida
      if (e.altKey && e.key >= '0' && e.key <= '9') {
        const index = parseInt(e.key, 10);
        // Atalho para os primeiros 10 itens de navegação principal
        const navItems = document.querySelectorAll('.nav-item > a');
        
        if (index === 0 && navItems.length > 9) {
          navItems[9].click(); // Alt+0 acessa o décimo item
        } else if (index > 0 && navItems.length >= index) {
          navItems[index - 1].click();
        }
        
        e.preventDefault();
      }
      
      // Ctrl+K ou Cmd+K para busca global
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        // Acionar busca global
        this.pushEvent("toggle_global_search");
        e.preventDefault();
      }
    });
  },
  
  // Configurar manipuladores de eventos para fechar menus
  setupEventHandlers() {
    document.addEventListener('click', e => {
      // Se o clique foi fora da navegação, fechar dropdowns abertos
      if (!this.el.contains(e.target)) {
        // Implementação específica para fechar dropdowns
      }
    });
  },
  
  updated() {
    // Não precisamos recarregar o estado aqui pois AlpineJS mantém
    // seu estado interno entre atualizações
  },
  
  beforeDestroy() {
    // Limpar observer
    if (this.observer) {
      this.observer.disconnect();
    }
    
    // Salvar estado final
    this.saveExpandedState();
  }
};

/**
 * Hook para adaptação responsiva dos breadcrumbs
 * - Trunca elementos conforme largura disponível
 * - Mantém sempre visíveis primeiro e último elemento
 */
const BreadcrumbsResponsive = {
  mounted() {
    this.updateBreadcrumbsVisibility();
    
    // Atualizar quando a janela for redimensionada
    window.addEventListener('resize', this.debouncedUpdate.bind(this));
  },
  
  // Função debounced para evitar muitas chamadas durante resize
  debouncedUpdate() {
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
    
    this.resizeTimer = setTimeout(() => {
      this.updateBreadcrumbsVisibility();
    }, 200);
  },
  
  // Atualiza a visibilidade dos itens de breadcrumb com base na largura
  updateBreadcrumbsVisibility() {
    const breadcrumbs = this.el;
    const items = breadcrumbs.querySelectorAll('li');
    
    if (items.length <= 2) return; // Nada a ajustar se temos 2 ou menos itens
    
    const containerWidth = breadcrumbs.offsetWidth;
    const firstItem = items[0];
    const lastItem = items[items.length - 1];
    
    // Resetar configuração
    items.forEach(item => {
      item.style.display = '';
    });
    
    // Se o espaço é suficiente, mostrar tudo
    const totalWidth = Array.from(items).reduce((sum, item) => sum + item.offsetWidth, 0);
    
    if (totalWidth <= containerWidth) return;
    
    // Início com primeiro e último itens sempre visíveis
    let availableWidth = containerWidth - firstItem.offsetWidth - lastItem.offsetWidth;
    
    // Esconder itens do meio conforme necessário
    for (let i = items.length - 2; i > 0; i--) {
      if (availableWidth <= 0) {
        items[i].style.display = 'none';
      } else {
        availableWidth -= items[i].offsetWidth;
        
        if (availableWidth < 0) {
          items[i].style.display = 'none';
        }
      }
    }
  },
  
  beforeDestroy() {
    window.removeEventListener('resize', this.debouncedUpdate.bind(this));
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
  }
};

// Export using CommonJS format instead of ES6 modules
module.exports = {
  HierarchyNav,
  BreadcrumbsResponsive
};